import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { buildLegacyInterviewPayload } from 'features/sendouts/common/utils';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { FileStatus } from 'UI/constants/status';
import { VALIDATION_REGEXS } from 'UI/utils';

export const ATTACHMENT_FIELD_KEYS = ['file_24', 'file_25', 'file_26'];

const EMAIL_KEYS = ['work_email', 'email'];

const normalizeEmail = email => email.trim().toLowerCase();

const extractEmail = item => {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';

  const emailKey = EMAIL_KEYS.find(key => typeof item[key] === 'string' && item[key].trim());

  return emailKey ? item[emailKey].trim() : '';
};

export const buildEmailList = (items = []) => {
  if (!Array.isArray(items)) return [];

  const seenEmails = new Set();

  return items.reduce((emails, item) => {
    const email = extractEmail(item);

    if (!email || !VALIDATION_REGEXS.EMAIL.test(email)) return emails;

    const normalizedEmail = normalizeEmail(email);

    if (seenEmails.has(normalizedEmail)) return emails;

    seenEmails.add(normalizedEmail);

    return [...emails, email];
  }, []);
};

export const buildAttachments = (_, formValues = {}) => {
  const allAttachments = ATTACHMENT_FIELD_KEYS.flatMap(fieldKey => formValues?.[fieldKey] || []);

  return {
    candidate: [...allAttachments],
    sendout: [...allAttachments],
    toSend: [...allAttachments]
  };
};

export const ATTACHMENT_SOURCES = { Candidate: 'candidate', Temporary: 'temporary' };

export const buildAttachmentReferences = (_, formValues = {}) =>
  ATTACHMENT_FIELD_KEYS.flatMap(fieldKey => formValues?.[fieldKey] || [])
    .filter(file => file?.id != null)
    .map(file => ({
      id: file.id,
      source:
        file?.type === FileStatus.New ? ATTACHMENT_SOURCES.Temporary : ATTACHMENT_SOURCES.Candidate
    }));

export const buildAttachmentFieldValues = (attachments = []) =>
  ATTACHMENT_FIELD_KEYS.reduce((fieldValues, fieldKey) => {
    const categoryId = Number(fieldKey.replace('file_', ''));

    return {
      ...fieldValues,
      [fieldKey]: attachments.filter(file => file?.file_type_id === categoryId)
    };
  }, {});

export const mapCancelEmailToSendEmail = value => !value;

export const buildNumberValue = value => {
  if (value == null || value === '') return null;

  const parsedValue = Number(value);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

export const buildLegacyInterviews = (_, formValues = {}) => {
  const interview = buildLegacyInterviewPayload(formValues);

  return interview ? [interview] : [];
};

export const checkIfCandidateIsAlreadyInSendout = async ({ candidateId, jobOrderId }) => {
  if (!candidateId || !jobOrderId) {
    return { isDuplicate: false, duplicates: [], error: null };
  }

  try {
    const params = { job_order_id: jobOrderId, candidate_id: candidateId };
    const queryParams = new URLSearchParams(params).toString();
    const response = await API.get(`${Endpoints.SendoutDuplicates}?${queryParams}`);

    if (response.status !== HTTPStatusCodes.Ok) {
      return { isDuplicate: false, duplicates: [], error: new Error('Duplicate check failed') };
    }

    const duplicates = Array.isArray(response.data?.data) ? response.data?.data : [];
    return { isDuplicate: duplicates.length > 0, duplicates };
  } catch (error) {
    return { isDuplicate: false, duplicates: [], error };
  }
};
